// src/utils/recurringInvoices.js  recinv-util-6a2f9c17
// Shared READ-ONLY helpers for the recurring-invoice UI (Seam 2a): a plain-English
// recurrence description and the health-signal derivation. The list and the detail
// view both import these so they agree on one source of truth — mirrors the
// isPaymentSyncWarningActive / needsQbAttention shared-predicate convention.
import { toJsDate } from './formatters';

// The generator/seed encode "never ends" as an endDate Timestamp at the year-2999
// sentinel (recurring-jobs convention; seedRecurringInvoice.js SENTINEL, generator
// NEVER_SENTINEL at index.js:1488). A naive endDate comparison would flag EVERY
// never-ending schedule as "ending soon", so every date check guards on this.
const NEVER_ENDS_YEAR = 2999;

const singularUnit = (unit) => ({ days: 'day', weeks: 'week', months: 'month', years: 'year' }[unit] || unit || '');

// "Every week" / "Every 2 weeks" / "Every month" / "Every 3 months".
export const describeRecurrence = (recurrence) => {
  if (!recurrence) return '—';
  switch (recurrence.frequency) {
    case 'Weekly':    return 'Every week';
    case 'Bi-Weekly': return 'Every 2 weeks';
    case 'Monthly':   return 'Every month';
    case 'Custom': {
      const n = Math.max(1, parseInt(recurrence.interval, 10) || 1);
      return n === 1 ? `Every ${singularUnit(recurrence.unit)}` : `Every ${n} ${recurrence.unit}`;
    }
    default: return '—';
  }
};

// Health signals — all derivable from the schedule doc, NO extra reads.
//   broken       : lastError set  -> hard failure (red)
//   attention[]  : overdue run / ending soon / unpaid backlog (amber)
// Returns { broken, brokenReason, attention:[...], level:'broken'|'attention'|'healthy' }.
export const scheduleHealth = (schedule, now = new Date()) => {
  const attention = [];
  const brokenReason = schedule?.lastError ? String(schedule.lastError) : null;

  // OVERDUE RUN: active schedule whose nextRunDate slipped into the past.
  const nextRun = toJsDate(schedule?.nextRunDate);
  if (schedule?.status === 'active' && nextRun && nextRun.getFullYear() < NEVER_ENDS_YEAR && nextRun < now) {
    attention.push('A scheduled run is overdue — an invoice should have generated and did not.');
  }

  // ENDING SOON: a real end date within 30 days. neverEnds is BOTH a stored boolean
  // AND the 2999 sentinel — guard both so a never-ending schedule never trips.
  const endDate = toJsDate(schedule?.recurrence?.endDate);
  if (schedule?.recurrence?.neverEnds !== true && endDate && endDate.getFullYear() < NEVER_ENDS_YEAR && endDate >= now) {
    const in30 = new Date(now); in30.setDate(in30.getDate() + 30);
    if (endDate <= in30) attention.push('This schedule ends within 30 days.');
  }

  // Softer signal: unpaid invoices this schedule has already generated.
  const unpaid = Number(schedule?.unpaidGeneratedCount || 0);
  if (unpaid > 0) attention.push(`${unpaid} generated invoice${unpaid === 1 ? '' : 's'} still unpaid.`);

  const level = brokenReason ? 'broken' : (attention.length ? 'attention' : 'healthy');
  return { broken: !!brokenReason, brokenReason, attention, level };
};

// "Needs attention" filter predicate — its own axis, mirrors needsQbAttention.
export const needsAttention = (schedule) => {
  const h = scheduleHealth(schedule);
  return h.broken || h.attention.length > 0;
};
